import { Component, Fragment, Host, Prop, h } from '@stencil/core';

export type ButtonColor = 'primary' | 'secondary' | 'tertiary' | 'start-action';
export type ButtonWidth = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl';
export type ButtonHeight = 'small' | 'medium' | 'large';
export type ButtonState = 'normal' | 'loading' | 'loaded';

@Component({
  tag: 'ico-button-animated',
  styleUrl: 'ico-button-animated.css',
})
export class IcoButtonAnimated {
  @Prop() color: ButtonColor = 'primary';
  @Prop() label: string = 'Button';
  @Prop() loadedLabel: string = 'Saved';
  @Prop() buttonState: ButtonState = 'normal';
  @Prop() width: ButtonWidth = 'md';
  @Prop() height: ButtonHeight = 'medium';
  @Prop() disabled: boolean = false;
  @Prop() url: string = '';
  @Prop() target: string = '_self';
  @Prop() whenClicked: () => void = () => {};

  handleClick() {
    if (!this.disabled) {
      this.whenClicked();
    }
  }

  render() {
    const classList = {
      'inline-flex outline-none box-border w-full': true, // position + sizing
      'items-center justify-center text-center no-underline font-montserrat text-[1.125em] rounded-[0.25em] tracking-normal font-extrabold': true, // typography
      'ico-button-animated--primary': this.color === 'primary',
      'ico-button-animated--secondary': this.color === 'secondary',
      'ico-button-animated--tertiary': this.color === 'tertiary',
      'ico-button-animated--start-action': this.color === 'start-action',
      'cursor-pointer animation-push': !this.disabled,
      'opacity-30 pointer-events-none select-none': this.disabled,
      'pointer-events-none': this.buttonState !== 'normal',

      'px-[0.25rem]': this.width === '2xs',
      'px-[0.5rem]': this.width === 'xs',
      'px-[0.75rem]': this.width === 'sm',
      'px-[1rem]': this.width === 'md',
      'px-[1.25rem]': this.width === 'lg',
      'px-[1.5rem]': this.width === '2xl',
      'px-[2rem]': this.width === '4xl',

      'h-[1.5rem]': this.height === 'small',
      'h-[2rem]': this.height === 'medium',
      'h-[3rem]': this.height === 'large',
    };

    const label = (
      <Fragment>
        <span class={{ invisible: this.buttonState !== 'normal', 'btn-animated-text-custom-by-theme': true }}>{this.label}</span>
        {this.buttonState === 'loading' && <Spinner />}
        {this.buttonState === 'loaded' && (
          <span class="flex absolute items-center space-x-1.5">
            <svg-icon width="1.5rem" icon="round-check-mark" class="h-6" />
            <span>{this.loadedLabel}</span>
          </span>
        )}
      </Fragment>
    );

    return (
      <Host>
        {this.url ? (
          <a href={this.url} onClick={() => this.handleClick()} target={this.target} class={classList}>
            {label}
          </a>
        ) : (
          <button onClick={() => this.handleClick()} disabled={this.disabled} class={classList}>
            {label}
          </button>
        )}
      </Host>
    );
  }
}

const Spinner = () => (
  <div class="flex absolute items-center justify-center">
    <div class="spinner"></div>
  </div>
);
